/**
 * ToolActivityLog
 *
 * Cursor-style expandable list of action calls for an assistant message.
 * Reads from message.annotation; each entry shows name, duration, and
 * expandable result. Renders during streaming as annotations update.
 *
 * Primary responsibilities:
 * - Derive action entries from annotation (result + requestTime/responseTime)
 * - Render one row per action (collapsed: icon, label, duration)
 * - Expand row to show result (truncated, "Show more" if large)
 */

'use client'

import React, { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import {
  IconApi,
  IconArchive,
  IconBrain,
  IconCalendar,
  IconChartBar,
  IconChecklist,
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconCode,
  IconCube,
  IconDatabase,
  IconDeviceLaptop,
  IconEdit,
  IconCpu,
  IconPhoto,
  IconFileText,
  IconFileSpreadsheet,
  IconFolder,
  IconFlag,
  IconLayoutDashboard,
  IconLink,
  IconMail,
  IconMessages,
  IconMicrophone,
  IconNotes,
  IconPalette,
  IconPaperclip,
  IconPresentation,
  IconRobot,
  IconSearch,
  IconTable,
  IconUsers,
  IconVideo,
  IconWorld
} from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { getToolDisplayLabel } from '@/lib/tool-labels'
import { getGoogleFaviconUrl, getDomainFromUrl } from '@/lib/favicon'
import {
  renderToolResultForEntry,
  shouldDefaultExpandToolResult
} from '@/components/messages/annotations/tool-results'
import type { Annotation, Annotation2 } from '@/types/annotation'

const ICON_CLASS = 'size-4 shrink-0 text-muted-foreground'
const WEB_SEARCH_FAVICON_COUNT = 5

const NON_TOOL_ANNOTATION_KEYS = new Set([
  'reasoning_summary',
  'editableContent',
  'losi_steps',
  'show_plan',
  'sidekick_tool_active',
  'sidekick_tool_done',
  'toolCalls',
  'selected_html_elements',
  'search_status',
  'search_detailed_status',
  'set_todos',
  'model_switch',
  'free_plan_tool_limit',
  'tool_budget',
  'sidecar_run',
  'update_todo_status'
])

/** Keys that have dedicated rich cards; show those as cards, not in the activity log. */
const RICH_CARD_KEYS = new Set([
  'deepResearch',
  'platformTools',
  'webScraper__getWeather',
  'yahooFinance__get_stock_data',
  'currentTime__getCurrentTime'
])
function isRichCardKey(key: string): boolean {
  if (RICH_CARD_KEYS.has(key)) return true
  if (key.startsWith('sandbox__') || /codeInterpreter|codeExecutionTool/i.test(key)) return true
  if (key.startsWith('deepResearch') || key.startsWith('platformTools')) return true
  if (key.startsWith('GeneratedIntegrationTool__')) return true
  if (key.startsWith('crm__') || key.startsWith('nexus__')) return true
  if (key.startsWith('signposting__')) return true
  if (key.startsWith('CredentialVault__requestLogin')) return true
  if (key.startsWith('CredentialVault__requestSecret')) return true
  if (key.startsWith('computerUse__requestCredential')) return true
  if (key.startsWith('losiCode__request_connector')) return true
  if (key.startsWith('losiCode__check_coding_readiness')) return true
  if (key.startsWith('losiCode__')) return true
  if (key.startsWith('liveScores__')) return true
  if (key.startsWith('eventDiscovery__')) return true
  if (key.startsWith('priceComparison__')) return true
  if (key.startsWith('smartReminder__')) return true
  // MCP install returns a connect/auth card — promote like location/connector.
  if (key === 'MCPIntegrationTool__installMcpServer') return true
  if (key.startsWith('MCPIntegrationTool__') && key.toLowerCase().includes('install')) {
    return true
  }
  return false
}

function formatKeyAsLabel(key: string, result?: unknown): string {
  return getToolDisplayLabel(key, result)
}

function getLosiSpacesToolIcon(key: string): React.ReactNode {
  const normalized = key.toLowerCase()

  if (normalized.includes('spacefile')) return <IconPaperclip className={ICON_CLASS} />
  if (normalized.includes('spacecollection')) return <IconArchive className={ICON_CLASS} />
  if (normalized.includes('spacesheet')) return <IconTable className={ICON_CLASS} />
  if (normalized.includes('spacesprint')) return <IconFlag className={ICON_CLASS} />
  if (normalized.includes('spaceevent') || normalized.includes('calendar')) {
    return <IconCalendar className={ICON_CLASS} />
  }
  if (normalized.includes('spacedesign') || normalized.includes('whiteboard')) {
    return <IconPalette className={ICON_CLASS} />
  }
  if (normalized.includes('spacenote')) return <IconNotes className={ICON_CLASS} />
  if (normalized.includes('spacefolder')) return <IconFolder className={ICON_CLASS} />
  if (normalized.includes('spacechat') || normalized.includes('conversation')) {
    return <IconMessages className={ICON_CLASS} />
  }
  if (normalized.includes('member') || normalized.includes('invite')) {
    return <IconUsers className={ICON_CLASS} />
  }
  if (normalized.includes('linkeditem') || normalized.includes('attachable')) {
    return <IconLink className={ICON_CLASS} />
  }
  if (
    normalized.includes('task') ||
    normalized.includes('checklist') ||
    normalized.includes('subtask')
  ) {
    return <IconChecklist className={ICON_CLASS} />
  }
  if (normalized.includes('space')) return <IconLayoutDashboard className={ICON_CLASS} />

  return <IconApi className={ICON_CLASS} />
}

/** Extract up to N favicon URLs from web search result organic links. */
function getWebSearchFaviconUrls(result: unknown): string[] {
  const raw = result != null && typeof result === 'object' && 'result' in result
    ? (result as { result?: unknown }).result
    : result
  const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null
  const organic = Array.isArray(obj?.organic) ? obj.organic as Array<{ link?: string }> : []
  const urls: string[] = []
  for (let i = 0; i < Math.min(WEB_SEARCH_FAVICON_COUNT, organic.length); i++) {
    const link = organic[i]?.link
    if (typeof link === 'string') {
      const domain = getDomainFromUrl(link)
      const fav = domain ? getGoogleFaviconUrl(domain, 32) : null
      if (fav) urls.push(fav)
    }
  }
  return urls
}

function getToolIcon(key: string): React.ReactNode {
  if (key === 'set_todos' || key === 'update_todo_status') {
    return <IconChecklist className={ICON_CLASS} />
  }
  if (key === 'show_plan' || key === 'edit_canvas') {
    return <IconFileText className={ICON_CLASS} />
  }
  if (key === 'rename_chat') return <IconEdit className={ICON_CLASS} />
  if (key.startsWith('ContentEngine__')) return <IconCpu className={ICON_CLASS} />
  if (key.startsWith('MCPIntegrationTool__')) return <IconApi className={ICON_CLASS} />
  if (key.startsWith('SkillIntegrationTool__')) return <IconBrain className={ICON_CLASS} />
  if (key.startsWith('sandbox__') || key === 'sandbox') {
    return <IconDeviceLaptop className={ICON_CLASS} />
  }
  if (key.startsWith('losiSpaces__')) {
    return getLosiSpacesToolIcon(key)
  }
  if (key.startsWith('VirtualFilesystem__')) return <IconFolder className={ICON_CLASS} />
  if (key.startsWith('MemoryTool__') || /memory/i.test(key)) return <IconBrain className={ICON_CLASS} />
  if (key.startsWith('ChatManagementTool__') || key.startsWith('ChatSearch__')) {
    return <IconMessages className={ICON_CLASS} />
  }
  if (key.startsWith('BookmarkSearch__') || key.startsWith('StarredMessagesSearch__')) {
    return <IconLink className={ICON_CLASS} />
  }
  if (key.startsWith('ConnectedServices__') || key.startsWith('ToolManagementTool__')) {
    return <IconLink className={ICON_CLASS} />
  }
  if (key.includes('googleSearch') || key.includes('FetchDataFromUrl')) return <IconSearch className={ICON_CLASS} />
  if (key.includes('getWeather') || key.includes('youtubeCaptions')) return <IconWorld className={ICON_CLASS} />
  if (/imageGenerator|gptImageFull|flux1Pro|stableDiffusion/i.test(key)) return <IconPhoto className={ICON_CLASS} />
  if (/videoGenerator|studioVideoGenerator/i.test(key)) return <IconVideo className={ICON_CLASS} />
  if (key.includes('yahooFinance') || key.includes('get_stock')) return <IconChartBar className={ICON_CLASS} />
  if (key.includes('Sidecar') || key === 'sidecar_run') return <IconRobot className={ICON_CLASS} />
  if (/audioTool/i.test(key)) return <IconMicrophone className={ICON_CLASS} />
  if (/threeDAssetGenerator/i.test(key)) return <IconCube className={ICON_CLASS} />
  if (/currentTime/i.test(key)) return <IconClock className={ICON_CLASS} />
  if (/emailTool/i.test(key)) return <IconMail className={ICON_CLASS} />
  if (/smsTool/i.test(key)) return <IconMessages className={ICON_CLASS} />
  if (/slideCreator/i.test(key)) return <IconPresentation className={ICON_CLASS} />
  if (/documentGenerator/i.test(key)) return <IconFileText className={ICON_CLASS} />
  if (/spreadsheetTool/i.test(key)) return <IconFileSpreadsheet className={ICON_CLASS} />
  if (/codeInterpreter|codeExecutionTool/i.test(key)) return <IconCode className={ICON_CLASS} />
  if (/dataAnalysisTool/i.test(key)) return <IconDatabase className={ICON_CLASS} />
  if (/taskAutomation/i.test(key)) return <IconChecklist className={ICON_CLASS} />
  if (/websiteScreenshot/i.test(key)) return <IconPhoto className={ICON_CLASS} />
  if (/unifiedImage/i.test(key)) return <IconPhoto className={ICON_CLASS} />
  if (/modifyVideoTool/i.test(key)) return <IconVideo className={ICON_CLASS} />
  return <IconApi className={ICON_CLASS} />
}

export interface ToolActivityEntry {
  key: string
  name: string
  durationMs: number
  result: unknown
  request?: unknown
  status?: 'running' | 'progress' | 'complete' | 'error'
  /** For web search: favicon URLs from organic results (3–5). */
  faviconUrls?: string[]
}

function isToolEntry(
  val: unknown
): val is {
  result: unknown
  request?: unknown
  requestTime?: number
  responseTime?: number
  status?: 'running' | 'progress' | 'complete' | 'error'
} {
  if (!val || typeof val !== 'object') return false
  const o = val as Record<string, unknown>
  if (o.result === undefined) return false
  return 'requestTime' in o || 'responseTime' in o
}

function getDurationMs(val: { requestTime?: number; responseTime?: number }): number {
  const ms = val.requestTime ?? val.responseTime
  if (typeof ms === 'number') return ms
  return 0
}

const MAX_RESULT_PREVIEW = 400
const MAX_EXPANDED = 2000
const MAX_FULL = 50_000

export interface ToolActivityLogProps {
  annotation: Record<string, unknown> | null | undefined
  className?: string
}

export function ToolActivityLog({ annotation, className }: ToolActivityLogProps) {
  const entries = useMemo((): ToolActivityEntry[] => {
    if (!annotation || typeof annotation !== 'object') return []
    const list: ToolActivityEntry[] = []
    for (const key of Object.keys(annotation)) {
      if (NON_TOOL_ANNOTATION_KEYS.has(key)) continue
      if (isRichCardKey(key)) continue
      const val = annotation[key]
      if (!isToolEntry(val)) continue
      const result = val.result
      const name = formatKeyAsLabel(key, result)
      const faviconUrls =
        key === 'webScraper__googleSearch' || key.includes('googleSearch')
          ? getWebSearchFaviconUrls(result)
          : undefined
      list.push({
        key,
        name,
        durationMs: getDurationMs(val),
        result,
        request: val.request,
        status: val.status,
        faviconUrls
      })
    }
    return list
  }, [annotation])

  if (entries.length === 0) return null

  return (
    <div
      className={cn('border-border bg-muted/20 rounded-lg border p-2 text-sm', className)}
      data-testid="tool-activity-log"
      role="region"
      aria-label="Action activity"
    >
      <ul className="flex flex-col gap-0.5">
        {entries.map((entry) => (
          <ToolActivityRow key={entry.key} entry={entry} />
        ))}
      </ul>
    </div>
  )
}

function isVirtualFileResult(entry: ToolActivityEntry): { id: string; path: string; name: string; content?: string } | null {
  if (!entry.key.startsWith('VirtualFilesystem__losi__fs_')) return null
  const r = entry.result
  const obj = typeof r === 'string' ? (() => { try { return JSON.parse(r) as Record<string, unknown> } catch { return null } })() : (r && typeof r === 'object' ? (r as Record<string, unknown>) : null)
  if (!obj || typeof obj.id !== 'string' || typeof obj.path !== 'string') return null
  return {
    id: obj.id as string,
    path: obj.path as string,
    name: (obj.name as string) ?? obj.path.split('/').pop() ?? obj.path,
    content: typeof obj.content === 'string' ? obj.content : undefined
  }
}

function ToolActivityRow({ entry }: { entry: ToolActivityEntry }) {
  const { t } = useTranslation()
  const shouldDefaultExpand = useMemo(() => shouldDefaultExpandToolResult(entry), [entry])
  const [expanded, setExpanded] = useState(() => shouldDefaultExpand)
  const [showFull, setShowFull] = useState(false)
  const durationSec = entry.durationMs / 1000
  const maxLen = showFull ? MAX_FULL : expanded ? MAX_EXPANDED : MAX_RESULT_PREVIEW
  const resultPreview = formatResultPreview(entry.result, maxLen)
  const isTruncated = resultPreview.endsWith('…')
  const virtualFile = isVirtualFileResult(entry)
  const isWebSearch =
    entry.key === 'webScraper__googleSearch' || entry.key.includes('googleSearch')
  const renderedToolResult = useMemo(() => renderToolResultForEntry(entry), [entry])

  useEffect(() => {
    if (shouldDefaultExpand) {
      // Only flip when currently collapsed; setExpanded(true) while already true
      // is a no-op for React, but guarding keeps intent explicit and avoids any
      // redundant update churn during streaming re-renders.
      setExpanded((prev) => (prev ? prev : true))
    }
  }, [shouldDefaultExpand])

  return (
    <li className="hover:border-border/50 rounded-md border border-transparent">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="focus-visible:ring-ring flex w-full items-center gap-2 px-2 py-1.5 text-left focus:outline-none focus-visible:ring-2"
        aria-expanded={expanded}
      >
        <span className="text-muted-foreground shrink-0" aria-hidden>
          {expanded ? (
            <IconChevronDown className="size-4" />
          ) : (
            <IconChevronRight className="size-4" />
          )}
        </span>
        <span aria-hidden>{getToolIcon(entry.key)}</span>
        <span className="min-w-0 flex-1 truncate font-medium">{entry.name}</span>
        {entry.faviconUrls && entry.faviconUrls.length > 0 && (
          <span className="flex shrink-0 items-center -space-x-2" aria-hidden>
            {entry.faviconUrls.slice(0, WEB_SEARCH_FAVICON_COUNT).map((src, i) => (
              <span
                key={`${src}-${i}`}
                className="ring-background inline-block size-6 overflow-hidden rounded-full ring-2"
              >
                <Image
                  src={src}
                  alt=""
                  width={24}
                  height={24}
                  unoptimized
                  className="size-full object-cover"
                />
              </span>
            ))}
          </span>
        )}
        <span className="text-muted-foreground shrink-0 tabular-nums">
          {durationSec >= 1 ? `${durationSec.toFixed(1)}s` : `${entry.durationMs}ms`}
        </span>
      </button>
      {expanded && (
        <div className="border-border/50 border-t px-2 py-1.5 pl-8">
          {renderedToolResult ? (
            renderedToolResult
          ) : (
            <>
              {virtualFile && !isWebSearch && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    window.dispatchEvent(
                      new CustomEvent('openVirtualFileInCanvas', {
                        cancelable: true,
                        detail: { id: virtualFile.id, path: virtualFile.path, name: virtualFile.name, content: virtualFile.content }
                      })
                    )
                  }}
                  className="text-primary mb-2 block text-xs font-medium hover:underline"
                >
                  {t('Open')}
                </button>
              )}
              <pre className="text-muted-foreground max-h-60 overflow-auto whitespace-pre-wrap break-words font-mono text-xs">
                {resultPreview || (isWebSearch ? 'Web search results unavailable.' : '')}
              </pre>
              {isTruncated && !showFull && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowFull(true)
                  }}
                  className="text-primary mt-1 text-xs hover:underline"
                >
                  Show more
                </button>
              )}
            </>
          )}
        </div>
      )}
    </li>
  )
}

function formatResultPreview(result: unknown, maxLen: number): string {
  if (result === undefined || result === null) return ''

  if (typeof result === 'string') {
    try {
      const parsed = JSON.parse(result) as unknown
      return formatResultPreview(parsed, maxLen)
    } catch {
      return truncate(result, maxLen)
    }
  }

  if (typeof result === 'object') {
    const record = result as Record<string, unknown>
    const lines = Object.entries(record)
      .slice(0, 8)
      .map(([key, value]) => {
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          return `${key}: ${String(value)}`
        }

        if (Array.isArray(value)) {
          return `${key}: ${value.length} item${value.length === 1 ? '' : 's'}`
        }

        if (value && typeof value === 'object') {
          return `${key}: available`
        }

        return null
      })
      .filter((line): line is string => Boolean(line))

    if (lines.length > 0) {
      return truncate(lines.join('\n'), maxLen)
    }

    return 'Structured result available.'
  }

  return String(result)
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s
  return s.slice(0, max) + '…'
}

/**
 * Returns true if the annotation key is a "simple" tool-activity entry
 * (so we skip rendering its card and show it only in the activity log).
 * Rich-card keys (deepResearch, platformTools, crm__, nexus__) return false
 * so their cards still render.
 */
export function isToolActivityKey(key: string, annotation: Record<string, unknown>): boolean {
  if (NON_TOOL_ANNOTATION_KEYS.has(key)) return false
  if (isRichCardKey(key)) return false
  return isToolEntry(annotation[key])
}
